import { Button } from '@/components/ui/button';
import type { ConversationMessage } from '@/features/conversations/api';
import { resolveApiUrl } from '@/lib/resolve-api-url';
import { cn } from '@/lib/utils';
import { getApiClientConfig } from '@borradh-workspace/api-client';
import { useEffect, useState } from 'react';

const PLACEHOLDER_LABELS = new Set([
  '📷 Photo',
  '🎥 Video',
  '🎤 Voice message',
  '📎 File',
  '🔗 Shared link',
  '📎 Attachment',
  '[Sticker]',
  '[Message]',
]);

export function getRenderableMedia(message: ConversationMessage) {
  const meta = message.metadata;
  if (!meta || typeof meta !== 'object') return [];
  const items: Array<{ url: string; kind: 'image' | 'sticker' }> = [];
  const attachments = (
    meta as { attachments?: Array<{ type?: string; url?: string }> }
  ).attachments;
  if (Array.isArray(attachments)) {
    for (const attachment of attachments) {
      if (attachment?.url && attachment.type === 'image') {
        items.push({ url: attachment.url, kind: 'image' });
      }
    }
  }
  const stickerUrl = (meta as { stickerUrl?: string }).stickerUrl;
  if (stickerUrl) items.push({ url: stickerUrl, kind: 'sticker' });
  return items;
}

export function shouldShowMessageText(message: ConversationMessage): boolean {
  const content = message.content?.trim() ?? '';
  return (
    content.length > 0 &&
    !(getRenderableMedia(message).length > 0 && PLACEHOLDER_LABELS.has(content))
  );
}

export function ConversationMessageMedia({
  message,
}: { message: ConversationMessage }) {
  const attachments = (
    message.metadata as {
      attachments?: Array<{
        type?: string;
        mediaId?: string;
        url?: string;
        title?: string;
      }>;
    } | null
  )?.attachments;
  return (
    <>
      {getRenderableMedia(message).map((media) => (
        <img
          key={media.url}
          src={media.url}
          alt={media.kind === 'sticker' ? 'Sticker' : 'Attachment'}
          className={cn(
            'rounded-md object-contain',
            media.kind === 'sticker' ? 'max-h-24' : 'max-h-60 w-full'
          )}
          loading="lazy"
        />
      ))}
      {attachments?.map((attachment, index) =>
        attachment.mediaId && !attachment.url ? (
          <WhatsappMessageMedia
            key={`${attachment.mediaId}-${index}`}
            message={message}
            index={index}
            type={attachment.type}
            title={attachment.title}
          />
        ) : null
      )}
    </>
  );
}

function WhatsappMessageMedia({
  message,
  index,
  type,
  title,
}: {
  message: ConversationMessage;
  index: number;
  type?: string;
  title?: string;
}) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failure, setFailure] = useState<'unavailable' | 'retryable' | null>(
    null
  );
  const [attempt, setAttempt] = useState(0);
  // The retry counter deliberately restarts this request after a user action.
  // biome-ignore lint/correctness/useExhaustiveDependencies: attempt is the explicit retry trigger.
  useEffect(() => {
    setObjectUrl(null);
    setFailure(null);
    const controller = new AbortController();
    let url: string | null = null;
    void (async () => {
      try {
        const token = await getApiClientConfig()?.authProvider?.();
        const response = await fetch(
          resolveApiUrl(
            `conversations/${encodeURIComponent(message.conversationId)}/messages/${encodeURIComponent(message.id)}/media/${index}`
          ),
          {
            credentials: 'include',
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            signal: controller.signal,
            cache: 'no-store',
          }
        );
        if (!response.ok) {
          if (!controller.signal.aborted)
            setFailure(response.status === 404 ? 'unavailable' : 'retryable');
          return;
        }
        const blob = await response.blob();
        if (controller.signal.aborted) return;
        url = URL.createObjectURL(blob);
        setObjectUrl(url);
      } catch {
        if (!controller.signal.aborted) setFailure('retryable');
      }
    })();
    return () => {
      controller.abort();
      if (url) URL.revokeObjectURL(url);
    };
  }, [message.conversationId, message.id, index, attempt]);

  if (failure) {
    return (
      <div role="status" className="mt-2 text-sm text-muted-foreground">
        <p>
          {failure === 'unavailable'
            ? 'This attachment is no longer available from WhatsApp. Ask the customer to resend it.'
            : 'Could not load this attachment.'}
        </p>
        {failure === 'retryable' ? (
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => setAttempt((value) => value + 1)}
          >
            Try again
          </Button>
        ) : null}
      </div>
    );
  }
  if (!objectUrl)
    return (
      <p role="status" className="mt-2 text-sm text-muted-foreground">
        Loading attachment…
      </p>
    );
  if (type === 'image') {
    return (
      <img
        src={objectUrl}
        alt="Customer attachment"
        className="max-h-60 w-full rounded-md object-contain"
      />
    );
  }
  if (type === 'audio') {
    return (
      // biome-ignore lint/a11y/useMediaCaption: Customer voice notes do not include a transcript or caption track.
      <audio controls src={objectUrl} aria-label="Customer voice message" />
    );
  }
  if (type === 'video') {
    return (
      // biome-ignore lint/a11y/useMediaCaption: Customer videos do not include a caption track.
      <video controls src={objectUrl} className="max-h-60 w-full rounded-md" />
    );
  }
  if (type === 'file') {
    const fileName = (title || 'attachment')
      .replace(/[\\/:*?"<>|\r\n]/g, '_')
      .slice(0, 120);
    return (
      <a href={objectUrl} download={fileName} className="underline">
        Download {title || 'attachment'}
      </a>
    );
  }
  return null;
}
